import { BOMComponent } from '@/types/bom';
import { supabase } from '@/lib/supabase/client';
import { BOMComponentState } from './types';
import { logger } from './logger';
import { buildComponentHierarchy } from './utils';
import { BOMConfig } from './types';
import { safeUUID, cleanObject } from './utils';

/**
 * Create a slice with component operations
 */
export const createBOMComponentSlice = (
  set: (state: Partial<BOMComponentState> | ((state: BOMComponentState) => Partial<BOMComponentState>)) => void,
  get: () => BOMComponentState
): BOMComponentState => ({
  // Initialize state properties
  isLoading: false,
  error: null,
  componentsCache: {},
  
  fetchBOMComponents: async (bomId) => {
    try {
      // Always clear isLoading first to prevent stuck states
      set({ isLoading: false });
      
      // Force a small delay to ensure loading state is reset in React
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Now set the loading state to true
      set({ isLoading: true, error: null });
      
      // Check for recent cache
      const cache = get().componentsCache[bomId];
      const now = Date.now();
      
      logger.debug(`Fetching components for BOM ${bomId}, cache ${cache ? 'exists' : 'missing'}`);
      
      if (cache && (now - cache.timestamp < BOMConfig.cacheTimeMs)) {
        logger.debug(`Using cached data for BOM ${bomId}, ${cache.data.length} components`);
        
        // Return cached data but still refresh cache in background for future use
        setTimeout(() => {
          supabase
            .from('bom_components')
            .select(`*`)
            .eq('bom_id', bomId)
            .order('level', { ascending: true })
            .order('sequence_order', { ascending: true })
            .then(({ data }) => {
              if (data) {
                logger.debug(`Background updated cache for BOM ${bomId}, ${data.length} components`);
                set(state => ({
                  componentsCache: {
                    ...state.componentsCache,
                    [bomId]: {
                      timestamp: Date.now(),
                      data: data
                    }
                  }
                }));
              }
            });
        }, 100);
        
        set({ isLoading: false });
        return cache.data;
      }
      
      // No recent cache, fetch from DB
      logger.debug(`No recent cache for BOM ${bomId}, fetching from database`);
      
      // Explicit ordering by sequence_order to ensure components appear in the correct order
      const { data, error } = await supabase
        .from('bom_components')
        .select(`*`)
        .eq('bom_id', bomId)
        .order('level', { ascending: true }) 
        .order('sequence_order', { ascending: true })
        .order('created_at', { ascending: true }); // Fallback ordering
      
      if (error) {
        logger.error(`Error fetching BOM components: ${error.message}`);
        throw error;
      }
      
      // Update cache
      const components: BOMComponent[] = data || [];
      logger.debug(`Fetched ${components.length} components for BOM ${bomId}`);
      
      set(state => ({
        componentsCache: {
          ...state.componentsCache,
          [bomId]: {
            timestamp: now,
            data: components
          }
        }
      }));
      
      // Wait a small amount of time to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 50));
      
      return components;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch BOM components';
      logger.error(`Error in fetchBOMComponents: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      logger.debug(`Setting isLoading to false for BOM ${bomId}`);
      set({ isLoading: false });
    }
  },

  addBOMComponent: async (bomId, data) => {
    try {
      // Validate the bomId
      if (!bomId || typeof bomId !== 'string' || bomId.trim() === '') {
        logger.error('Invalid bomId provided:', bomId);
        throw new Error('BOM ID is required and must be a valid UUID');
      }

      // Validate required component data
      if (!data || !data.component_name) {
        logger.error('Invalid component data provided:', data);
        throw new Error('Component name is required');
      }

      logger.debug(`Adding component to BOM ${bomId}: ${data.component_name}`);
      set({ isLoading: true, error: null });

      // Calculate level based on parent (if provided)
      let level = 0;
      if (data.parent_component_id) {
        // Make sure parent_component_id is a valid UUID string
        const parentId = safeUUID(data.parent_component_id);
        if (!parentId) {
          logger.error(`Invalid parent_component_id format: ${data.parent_component_id}`);
          throw new Error('Invalid parent component ID format');
        }
          
        logger.debug(`Getting parent level for parentId=${parentId}`);
        
        const { data: parentComponent, error: parentError } = await supabase
          .from('bom_components')
          .select('level')
          .eq('id', parentId)
          .single();

        if (parentError) {
          logger.error(`Error fetching parent component: ${parentError.message}`);
          throw parentError;
        }
        
        level = (parentComponent?.level || 0) + 1;
        logger.debug(`Parent component level is ${parentComponent?.level}, new component level will be ${level}`);
      }

      // Clean up the data object
      const cleanData = cleanObject(data);
      
      // Create a component to insert - using a simple direct approach
      try {
        // Ensure parent_component_id is a valid UUID before insertion 
        // Note: Be careful not to pass non-UUID strings as this will cause comparison errors
        let parentId = null;
        if (cleanData.parent_component_id) {
          parentId = safeUUID(cleanData.parent_component_id);
          if (!parentId && cleanData.parent_component_id) {
            logger.warn(`Invalid parent_component_id format (${cleanData.parent_component_id}), setting to null`);
            // Instead of throwing an error, we'll set to null for parent_component_id
          }
        }
        
        // Ensure bom_id is a valid UUID - this is critical so we throw an error if invalid
        const validBomId = safeUUID(bomId);
        if (!validBomId) {
          logger.error(`Invalid BOM ID format: ${bomId}`);
          throw new Error('Invalid BOM ID format');
        }
        
        // For raw_material_id also ensure UUID validity
        let rawMaterialId = null;
        if (cleanData.raw_material_id) {
          rawMaterialId = safeUUID(cleanData.raw_material_id);
          if (!rawMaterialId && cleanData.raw_material_id) {
            logger.warn(`Invalid raw_material_id format (${cleanData.raw_material_id}), setting to null`);
          }
        }
        
        // Create component object with properly handled UUID fields
        const componentData = {
          component_name: cleanData.component_name,
          component_type: cleanData.component_type || 'assembly',
          parent_component_id: parentId, // Properly typed as UUID or null
          bom_id: validBomId, // Valid UUID
          raw_material_id: rawMaterialId, // Properly typed as UUID or null
          quantity: cleanData.quantity || 1,
          unit: cleanData.unit || 'Nos',
          status: cleanData.status || 'pending',
          level: level,
          drawing_number: cleanData.drawing_number || null,
          material: cleanData.material || null,
          finishing_size: cleanData.finishing_size || null,
          raw_material_size: cleanData.raw_material_size || null,
          // Ensure specifications contains processes if it was intended to be there
          specifications: cleanData.specifications || null, 
          remarks: cleanData.remarks || null, // Added remarks as it's a direct column
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        logger.debug(`Inserting component with data:`, {
          component_name: componentData.component_name,
          parent_id: parentId, 
          bom_id: validBomId
        });
        
        // Use direct insertion with proper type handling and explicit UUID casting
        const { data: insertResult, error: insertError } = await supabase
          .from('bom_components')
          .insert([componentData])
          .select('*')
          .single();
          
        if (insertError) {
          logger.error(`Error inserting component: ${insertError.message}`);
          throw insertError;
        }
        
        if (!insertResult) {
          throw new Error('Failed to create component - no result returned');
        }
        
        // Clear cache for this bomId
        logger.debug(`Clearing cache for BOM ${bomId} after component insertion`);
        set(state => ({
          componentsCache: {
            ...state.componentsCache,
            [bomId]: null
          }
        }));
        
        return insertResult as BOMComponent;
      } catch (err) {
        logger.error(`Component insertion failed: ${err instanceof Error ? err.message : String(err)}`);
        throw err;
      }
    } catch (err) {
      logger.error(`Error in addBOMComponent: ${err instanceof Error ? err.message : String(err)}`);
      set({ error: err instanceof Error ? err.message : 'Failed to add BOM component' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateBOMComponent: async (id, data) => {
    try {
      logger.debug(`Updating component ${id}`);
      set({ isLoading: true, error: null });

      // Make a clean copy of data
      const updateData = cleanObject(data);

      // If parent is changing, recalculate level
      if ('parent_component_id' in updateData) {
        let level = 0;
        if (updateData.parent_component_id) {
          // Ensure parent_component_id is properly handled
          const parentId = safeUUID(updateData.parent_component_id);
          if (!parentId) {
            logger.error(`Invalid parent_component_id for update: ${updateData.parent_component_id}`);
            throw new Error('Invalid parent component ID format');
          }
          
          updateData.parent_component_id = parentId;

          logger.debug(`Getting parent level for ID ${parentId}`);
          const { data: parentComponent, error: parentError } = await supabase
            .from('bom_components')
            .select('level')
            .eq('id', parentId)
            .single();

          if (parentError) {
            logger.error(`Error fetching parent: ${parentError.message}`);
            throw parentError;
          }
          
          level = (parentComponent?.level || 0) + 1;
        }
        
        (updateData as any).level = level;
        logger.debug(`Set component level to ${level} based on parent`);
      }

      // Get the BOM ID to invalidate cache
      logger.debug(`Fetching BOM ID for component ${id}`);
      const { data: component, error: fetchError } = await supabase
        .from('bom_components')
        .select('bom_id')
        .eq('id', id)
        .single();
        
      if (fetchError) {
        logger.error(`Error fetching component: ${fetchError.message}`);
        throw fetchError;
      }
      
      const bomId = component?.bom_id;
      logger.debug(`Component ${id} belongs to BOM ${bomId}`);

      // Update component directly
      logger.debug(`Updating component data`, updateData);
      const { error } = await supabase
        .from('bom_components')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        logger.error(`Error updating component: ${error.message}`);
        throw error;
      }

      // Clear cache for this bomId
      if (bomId) {
        logger.debug(`Clearing cache for BOM ${bomId} after component update`);
        set(state => ({
          componentsCache: {
            ...state.componentsCache,
            [bomId]: null
          }
        }));
      }
      
      logger.debug(`Component ${id} updated successfully`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update BOM component';
      logger.error(`Component update failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteBOMComponent: async (id: string) => {
    try {
      logger.debug(`Attempting to delete component with id: ${id}`);

      if (!id || typeof id !== 'string') {
        const err = new Error('Invalid component ID provided for deletion.');
        logger.error(`[BOM] ${err.message}`, { id });
        set({ error: err.message, isLoading: false });
        throw err;
      }
      set({ isLoading: true, error: null });

      // Get the BOM ID and component details
      const { data: component, error: fetchComponentError } = await supabase
        .from('bom_components')
        .select('*')
        .eq('id', id)
        .single();
        
      if (fetchComponentError) {
        logger.error(`Error fetching component ${id}: ${fetchComponentError.message}`);
        // If the component doesn't exist, consider it "deleted"
        if (fetchComponentError.code === 'PGRST116') {
          logger.info(`Component ${id} already deleted, operation succeeded`);
          return; // Exit successfully
        }
        throw fetchComponentError;
      }
      
      const bomId = component?.bom_id;
      logger.debug(`Deleting component ${id} from BOM ${bomId}`);

      // Simple deletion without trying to use database functions
      // Delete the component directly
      const { error: deleteError } = await supabase
        .from('bom_components')
        .delete()
        .eq('id', id);
        
      if (deleteError) {
        logger.error(`Error deleting component: ${deleteError.message}`);
        throw deleteError;
      }
      
      // Clear cache for this bomId
      if (bomId) {
        logger.debug(`Clearing cache for BOM ${bomId} after component deletion`);
        set(state => ({
          componentsCache: {
            ...state.componentsCache,
            [bomId]: null
          }
        }));
      }
      
      logger.info(`Component ${id} deleted successfully`);
    } catch (err) {
      logger.error(`Delete component error: ${String(err)}`);
      const message = err instanceof Error ? err.message : 'Failed to delete BOM component';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateComponentSequence: async (id, newSequence) => {
    try {
      logger.debug(`[BOMComponentOps] Updating component ${id} sequence to ${newSequence}`);
      set({ isLoading: true, error: null });

      // Get component details including BOM ID and parent ID
      const { data: component, error: fetchError } = await supabase
        .from('bom_components')
        .select('*')
        .eq('id', id)
        .single();
        
      if (fetchError) {
        logger.error(`[BOMComponentOps] Error fetching component: ${fetchError.message}`);
        throw fetchError;
      }
      
      const bomId = component?.bom_id;
      const parentId = component?.parent_component_id;
      const oldSequence = component?.sequence_order;
      
      logger.debug(`[BOMComponentOps] Got component details - BOM: ${bomId}, Parent: ${parentId || 'ROOT'}, Old Sequence: ${oldSequence}, New Sequence: ${newSequence}`);
      
      // First, aggressively clear cache immediately to prevent stale data issues
      if (bomId) {
        logger.debug(`[BOMComponentOps] Immediately clearing cache for BOM ${bomId}`);
        set(state => ({
          componentsCache: {
            ...state.componentsCache,
            [bomId]: null
          }
        }));
      }
      
      // Use the enhanced database function to update sequence order
      // This function now handles sibling component reordering too
      const { error } = await supabase
        .rpc('update_bom_component_sequence_order', {
          component_id: id,
          new_sequence: newSequence
        });
      
      if (error) {
        logger.error(`[BOMComponentOps] Error updating component sequence: ${error.message}`);
        throw error;
      }

      // Immediately start multiple progressive refreshes to ensure UI consistency
      if (bomId) {
        // Set up refresh intervals using exponential backoff
        // This ensures we get the latest data as it propagates through the database
        const refreshIntervals = [50, 200, 500, 1000, 2000];
        
        // Execute each refresh after its delay
        refreshIntervals.forEach((delay, index) => {
          setTimeout(async () => {
            logger.debug(`[BOMComponentOps] Executing refresh ${index+1}/${refreshIntervals.length} after ${delay}ms`);
            
            try {
              // Always use fresh fetch with no caching
              const { data: refreshedComponents, error: refreshError } = await supabase
                .from('bom_components')
                .select(`*`)
                .eq('bom_id', bomId)
                .order('level', { ascending: true })
                .order('sequence_order', { ascending: true });
                
              if (!refreshError && refreshedComponents) {
                logger.debug(`[BOMComponentOps] Refresh ${index+1}: Found ${refreshedComponents.length} components`);
                
                // Update cache with fresh data
                set(state => ({
                  componentsCache: {
                    ...state.componentsCache,
                    [bomId]: {
                      timestamp: Date.now(),
                      data: refreshedComponents
                    }
                  }
                }));
              } else if (refreshError) {
                logger.warn(`[BOMComponentOps] Refresh ${index+1} error: ${refreshError.message}`);
              }
            } catch (refreshErr) {
              logger.warn(`[BOMComponentOps] Refresh ${index+1} exception: ${String(refreshErr)}`);
            }
          }, delay);
        });
        
        // As a final measure, run the normalize_bom_component_sequences function
        // This will fix any sequence issues that might have accumulated
        setTimeout(async () => {
          try {
            logger.debug(`[BOMComponentOps] Running sequence normalization for BOM ${bomId}`);
            await supabase.rpc('normalize_bom_component_sequences', { bom_id_param: bomId });
          } catch (error) {
            logger.warn(`[BOMComponentOps] Sequence normalization error: ${String(error)}`);
          }
        }, 3000);
      }
      
      logger.info(`[BOMComponentOps] Component ${id} sequence update initiated`);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update component sequence';
      logger.error(`[BOMComponentOps] Sequence update failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },
  
  moveBOMComponent: async (id, newParentId) => {
    try {
      logger.debug(`Moving component ${id} to parent ${newParentId || 'root'}`);
      set({ isLoading: true, error: null });

      // Get the BOM ID to invalidate cache
      const { data: component, error: fetchComponentError } = await supabase
        .from('bom_components')
        .select('bom_id')
        .eq('id', id)
        .single();
        
      if (fetchComponentError) {
        logger.error(`Error fetching component: ${fetchComponentError.message}`);
        throw fetchComponentError;
      }
      
      const bomId = component?.bom_id;
      logger.debug(`Component ${id} belongs to BOM ${bomId}`);

      // Ensure parent ID is properly handled
      let parentId = newParentId ? safeUUID(newParentId) : null;
      if (newParentId && !parentId) {
        logger.error(`Invalid parent ID format: ${newParentId}`);
        throw new Error('Invalid parent ID format');
      }

      // Calculate new level
      let level = 0;
      if (newParentId) {
        logger.debug(`Calculating new level based on parent ${newParentId}`);
        const { data: parentComponent, error: parentError } = await supabase
          .from('bom_components')
          .select('level')
          .eq('id', newParentId)
          .single();

        if (parentError) {
          logger.error(`Error fetching parent level: ${parentError.message}`);
          throw parentError;
        }
        
        level = (parentComponent?.level || 0) + 1;
        logger.debug(`New level will be ${level}`);
      }

      // Update component directly
      logger.debug(`Updating component position`);
      const { error } = await supabase
        .from('bom_components')
        .update({
          parent_component_id: parentId,
          level,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        logger.error(`Error moving component: ${error.message}`);
        throw error;
      }

      // Clear cache for this bomId
      if (bomId) {
        logger.debug(`Clearing cache for BOM ${bomId} after component move`);
        set(state => ({
          componentsCache: {
            ...state.componentsCache,
            [bomId]: null
          }
        }));
      }
      
      logger.info(`Component ${id} moved successfully to ${newParentId || 'root'}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to move BOM component';
      logger.error(`Component move failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  buildBOMHierarchy: (components) => {
    return buildComponentHierarchy(components);
  },

  getBOMHierarchy: async (bomId) => {
    try {
      logger.debug(`Getting hierarchy for BOM ${bomId}`);
      // Use the cached versions of these methods
      const components = await get().fetchBOMComponents(bomId);
      return get().buildBOMHierarchy(components);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get BOM hierarchy';
      logger.error(`Hierarchy retrieval failed: ${message}`);
      set({ error: message });
      throw err;
    }
  },
  
  // Batch insert components for improved performance
  batchAddBOMComponents: async (bomId, parentComponentId, components) => {
    try {
      logger.debug(`Batch inserting ${components.length} components for BOM ${bomId}`);
      set({ isLoading: true, error: null });
      
      // Validate the bomId
      if (!bomId || typeof bomId !== 'string' || bomId.trim() === '') {
        logger.error('Invalid bomId provided:', bomId);
        throw new Error('BOM ID is required and must be a valid UUID');
      }
      
      // Ensure parentComponentId is properly handled
      let parentId = null;
      if (parentComponentId) {
        parentId = safeUUID(parentComponentId);
        if (!parentId && parentComponentId) {
          logger.warn(`Invalid parent_component_id format (${parentComponentId}), setting to null`);
        }
      }
      
      // Ensure valid BOM ID
      const validBomId = safeUUID(bomId);
      if (!validBomId) {
        logger.error(`Invalid BOM ID format: ${bomId}`);
        throw new Error('Invalid BOM ID format');
      }
      
      // Calculate level based on parent
      let level = 0;
      if (parentId) {
        logger.debug(`Getting parent level for parentId=${parentId}`);
        
        const { data: parentComponent, error: parentError } = await supabase
          .from('bom_components')
          .select('level')
          .eq('id', parentId)
          .single();

        if (parentError) {
          logger.error(`Error fetching parent component: ${parentError.message}`);
          throw parentError;
        }
        
        level = (parentComponent?.level || 0) + 1;
        logger.debug(`Parent level is ${parentComponent?.level}, new component level will be ${level}`);
      }
      
      // Format components for batch insertion
      const componentsArray = components.map(data => {
        // Clean up the data
        const cleanData = cleanObject(data);
        
        return {
          component_name: cleanData.component_name,
          component_type: cleanData.component_type || 'raw_material',
          parent_component_id: parentId,
          quantity: cleanData.quantity || 1,
          unit: cleanData.unit || 'Nos',
          status: cleanData.status || 'pending',
          finishing_size: cleanData.finishing_size || null,
          raw_material_size: cleanData.raw_material_size || null,
          remarks: cleanData.remarks || null
        };
      });
      
      // Use the batch_insert_bom_components RPC function
      const { error } = await supabase
        .rpc('batch_insert_bom_components', { 
          p_bom_id: validBomId,
          components: componentsArray
        });
        
      if (error) {
        logger.error(`Error batch inserting components: ${error.message}`);
        throw error;
      }
      
      // Clear cache for this bomId
      logger.debug(`Clearing cache for BOM ${bomId} after batch component insertion`);
      set(state => ({
        componentsCache: {
          ...state.componentsCache,
          [bomId]: null
        }
      }));
      
      logger.info(`Successfully batch inserted ${components.length} components`);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to batch insert BOM components';
      logger.error(`Batch insertion failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  // Clear cache for a specific BOM ID to force a fresh fetch
  clearCache: (bomId: string) => {
    logger.debug(`Manually clearing cache for BOM ${bomId}`);
    set(state => ({
      componentsCache: {
        ...state.componentsCache,
        [bomId]: null
      }
    }));
  }
});
